import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Reveal } from "../Reveal";
import { ArrowRight } from "lucide-react";

const SKYLINE = "https://images.unsplash.com/photo-1501324682324-0d8d7c4b9706?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NTYxNzV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBza3lzY3JhcGVyfGVufDB8fHx8MTc4NjMwMjkwNXww&ixlib=rb-4.1.0&q=85";
const services = ["Resume Writing", "LinkedIn Optimization", "Career Consulting", "The Full Package", "Not sure yet"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function LeadForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "Resume Writing", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (k === "email") setEmailError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(form.email.trim())) {
      setEmailError("Please enter a valid email address.");
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", { ...form, email: form.email.trim() });
      toast.success(data.message || "Thank you — we'll be in touch!");
      setForm({ name: "", email: "", phone: "", service: "Resume Writing", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-navy text-white overflow-hidden" data-testid="lead-section">
      <img src={SKYLINE} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-navy/85" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-36 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
        <Reveal>
          <div>
            <span className="overline text-cobalt-light">Start the conversation</span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-5 leading-[1.02]">
              Let's build the resume that gets the <span className="italic text-cobalt-light">yes.</span>
            </h2>
            <p className="text-white/60 mt-6 text-lg max-w-md">
              Tell us where you're headed. A senior consultant reviews every enquiry — no bots, no call centres.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} noValidate data-testid="lead-form" className="bg-white/[0.05] backdrop-blur-md border border-white/15 p-7 lg:p-9">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" testid="lead-name">
                <input required value={form.name} onChange={set("name")} data-testid="lead-name-input"
                  className="input" placeholder="Jane Cooper" />
              </Field>
              <Field label="Email" testid="lead-email">
                <input required type="email" value={form.email} onChange={set("email")} data-testid="lead-email-input"
                  className="input" placeholder="jane@email.com"
                  style={emailError ? { borderColor: "#f87171" } : undefined} />
                {emailError && <span className="text-xs text-red-300 mt-1 block" data-testid="lead-email-error">{emailError}</span>}
              </Field>
              <Field label="Phone" testid="lead-phone">
                <input required value={form.phone} onChange={set("phone")} data-testid="lead-phone-input"
                  className="input" placeholder="+91 88850 01332" />
              </Field>
              <Field label="Service" testid="lead-service">
                <select value={form.service} onChange={set("service")} data-testid="lead-service-select" className="input">
                  {services.map((s) => <option key={s} value={s} className="text-navy">{s}</option>)}
                </select>
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Anything we should know? (optional)" testid="lead-message">
                <textarea rows={3} value={form.message} onChange={set("message")} data-testid="lead-message-input"
                  className="input resize-none" placeholder="Target role, timeline, current challenges…" />
              </Field>
            </div>
            <button type="submit" disabled={submitting} data-testid="lead-submit-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-cobalt text-white font-medium hover:bg-white hover:text-navy transition-colors disabled:opacity-60">
              {submitting ? "Sending…" : "Request my consultation"}
              <ArrowRight size={18} />
            </button>
          </form>
        </Reveal>
      </div>

      <style>{`
        .input { width:100%; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.18); color:#fff; padding:0.75rem 0.9rem; font-size:0.95rem; outline:none; transition:border-color .2s; }
        .input::placeholder { color:rgba(255,255,255,0.4); }
        .input:focus { border-color:#3B82F6; }
        select.input option { background:#0A1128; }
      `}</style>
    </section>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="overline text-white/50 block mb-2">{label}</span>
      {children}
    </label>
  );
}
